import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  RowSelectionState,
  ColumnOrderState,
  PaginationState,
  OnChangeFn,
  Table,
} from '@tanstack/react-table';

/**
 * Table state that can be persisted
 */
export interface TableState {
  sorting?: SortingState;
  columnFilters?: ColumnFiltersState;
  columnVisibility?: VisibilityState;
  rowSelection?: RowSelectionState;
  columnOrder?: ColumnOrderState;
  pagination?: PaginationState;
  globalFilter?: string;
}

/**
 * Options for the useDataTable hook
 */
export interface UseDataTableOptions<TData> {
  /** The data to display in the table */
  data: TData[];
  /** Column definitions for the table */
  columns: ColumnDef<TData>[];
  /** Unique identifier for localStorage persistence */
  tableId: string;
  /** Enable sorting functionality (default: true) */
  enableSorting?: boolean | undefined;
  /** Enable filtering functionality (default: true) */
  enableFiltering?: boolean | undefined;
  /** Enable pagination functionality (default: true) */
  enablePagination?: boolean | undefined;
  /** Enable row selection functionality (default: false) */
  enableRowSelection?: boolean | undefined;
  /** Enable column visibility controls (default: true) */
  enableColumnVisibility?: boolean | undefined;
  /** Enable column ordering/reordering (default: false) */
  enableColumnOrdering?: boolean | undefined;
  /** Initial table state */
  initialState?: Partial<TableState> | undefined;
  /** Callback fired when state changes (for backend sync) */
  onStateChange?: ((state: TableState) => void) | undefined;
  /** Debounce delay for localStorage writes in ms (default: 500) */
  persistenceDebounce?: number | undefined;
  /** Default page size for pagination (default: 10) */
  defaultPageSize?: number | undefined;
  /** Available page size options (default: [10, 20, 50, 100]) */
  pageSizeOptions?: number[] | undefined;
  /** Enable state persistence to localStorage (default: true) */
  enablePersistence?: boolean | undefined;
  /** Enable manual pagination (for server-side pagination) */
  manualPagination?: boolean | undefined;
  /** Total row count (for server-side pagination) */
  rowCount?: number | undefined;
  /** Enable manual sorting (for server-side sorting) */
  manualSorting?: boolean | undefined;
  /** Enable manual filtering (for server-side filtering) */
  manualFiltering?: boolean | undefined;
}

/**
 * Return value from the useDataTable hook
 */
export interface UseDataTableReturn<TData> {
  /** TanStack Table instance */
  table: Table<TData>;
  /** Current sorting state */
  sorting: SortingState;
  /** Update sorting state */
  setSorting: OnChangeFn<SortingState>;
  /** Current column visibility state */
  columnVisibility: VisibilityState;
  /** Update column visibility state */
  setColumnVisibility: OnChangeFn<VisibilityState>;
  /** Current row selection state */
  rowSelection: RowSelectionState;
  /** Update row selection state */
  setRowSelection: OnChangeFn<RowSelectionState>;
  /** Current column filters state */
  columnFilters: ColumnFiltersState;
  /** Update column filters state */
  setColumnFilters: OnChangeFn<ColumnFiltersState>;
  /** Current global filter value */
  globalFilter: string;
  /** Update global filter value */
  setGlobalFilter: (value: string) => void;
  /** Current column order state */
  columnOrder: ColumnOrderState;
  /** Update column order state */
  setColumnOrder: OnChangeFn<ColumnOrderState>;
  /** Current pagination state */
  pagination: PaginationState;
  /** Update pagination state */
  setPagination: OnChangeFn<PaginationState>;
  /** Reset all table state to initial/default values */
  resetState: () => void;
  /** Export current table state as JSON */
  exportState: () => string;
  /** Import table state from JSON */
  importState: (stateJson: string) => void;
  /** Clear persisted state from localStorage */
  clearPersistedState: () => void;
}

/**
 * Generate a storage key for a specific table
 */
const getStorageKey = (tableId: string): string => `datatable_${tableId}_state`;

/**
 * Load table state from localStorage
 */
const loadPersistedState = (tableId: string): Partial<TableState> | null => {
  if (typeof window === 'undefined') return null;

  try {
    const key = getStorageKey(tableId);
    const stored = localStorage.getItem(key);

    if (!stored) return null;

    const parsed = JSON.parse(stored);

    // Validate the stored state structure
    if (typeof parsed !== 'object' || parsed === null) {
      console.warn('[useDataTable] Invalid persisted state format, ignoring');
      return null;
    }

    return parsed as Partial<TableState>;
  } catch (error) {
    console.error('[useDataTable] Failed to load persisted state:', error);
    return null;
  }
};

/**
 * Save table state to localStorage
 */
const savePersistedState = (tableId: string, state: TableState): void => {
  if (typeof window === 'undefined') return;

  try {
    const key = getStorageKey(tableId);
    localStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    console.error('[useDataTable] Failed to save persisted state:', error);
  }
};

/**
 * Clear table state from localStorage
 */
const clearPersistedState = (tableId: string): void => {
  if (typeof window === 'undefined') return;

  try {
    const key = getStorageKey(tableId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('[useDataTable] Failed to clear persisted state:', error);
  }
};

/**
 * Custom hook for managing data table state with TanStack Table v8
 *
 * Provides comprehensive state management for tables including:
 * - Sorting (single/multi-column)
 * - Filtering (column-specific and global)
 * - Pagination (client or server-side)
 * - Row selection (single/multi-row)
 * - Column visibility controls
 * - Column ordering/reordering
 * - State persistence to localStorage
 * - Optional backend synchronization
 *
 * @template TData - The shape of the data in each table row
 * @param options - Configuration options for the table
 * @returns Table instance and state management utilities
 *
 * @example
 * ```tsx
 * interface Person {
 *   id: string;
 *   name: string;
 *   age: number;
 * }
 *
 * const columns: ColumnDef<Person>[] = [
 *   { accessorKey: 'name', header: 'Name' },
 *   { accessorKey: 'age', header: 'Age' },
 * ];
 *
 * function PeopleTable() {
 *   const { table, globalFilter, setGlobalFilter } = useDataTable({
 *     data: people,
 *     columns,
 *     tableId: 'people-table',
 *     enableRowSelection: true,
 *   });
 *
 *   return (
 *     <div>
 *       <input
 *         value={globalFilter}
 *         onChange={(e) => setGlobalFilter(e.target.value)}
 *         placeholder="Search..."
 *       />
 *       <table>
 *         {table.getHeaderGroups().map(headerGroup => (
 *           // ... render headers
 *         ))}
 *         {table.getRowModel().rows.map(row => (
 *           // ... render rows
 *         ))}
 *       </table>
 *     </div>
 *   );
 * }
 * ```
 */
export function useDataTable<TData>({
  data,
  columns,
  tableId,
  enableSorting = true,
  enableFiltering = true,
  enablePagination = true,
  enableRowSelection = false,
  enableColumnVisibility = true,
  enableColumnOrdering = false,
  initialState = {},
  onStateChange,
  persistenceDebounce = 500,
  defaultPageSize = 10,
  pageSizeOptions: _pageSizeOptions = [10, 20, 50, 100],
  enablePersistence = true,
  manualPagination = false,
  rowCount,
  manualSorting = false,
  manualFiltering = false,
}: UseDataTableOptions<TData>): UseDataTableReturn<TData> {
  // Load persisted state on mount
  const persistedState = useMemo(() => {
    if (!enablePersistence) return null;
    return loadPersistedState(tableId);
  }, [tableId, enablePersistence]);

  // Merge initial state with persisted state (persisted takes precedence)
  const mergedInitialState = useMemo(
    () => ({
      ...initialState,
      ...persistedState,
    }),
    [initialState, persistedState]
  );

  // State management
  const [sorting, setSorting] = useState<SortingState>(
    mergedInitialState.sorting ?? []
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    mergedInitialState.columnFilters ?? []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    mergedInitialState.columnVisibility ?? {}
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    mergedInitialState.rowSelection ?? {}
  );
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    mergedInitialState.columnOrder ?? []
  );
  const [globalFilter, setGlobalFilter] = useState<string>(
    mergedInitialState.globalFilter ?? ''
  );
  const [pagination, setPagination] = useState<PaginationState>(
    mergedInitialState.pagination ?? {
      pageIndex: 0,
      pageSize: defaultPageSize,
    }
  );

  // Debounce timer ref for persistence
  const persistenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const stateChangeTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Get current complete state
  const getCurrentState = useCallback(
    (): TableState => ({
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      columnOrder,
      pagination,
      globalFilter,
    }),
    [sorting, columnFilters, columnVisibility, rowSelection, columnOrder, pagination, globalFilter]
  );

  // Debounced persistence effect
  useEffect(() => {
    if (!enablePersistence) return;

    // Clear existing timer
    if (persistenceTimerRef.current) {
      clearTimeout(persistenceTimerRef.current);
    }

    // Set new timer for debounced save
    persistenceTimerRef.current = setTimeout(() => {
      const state = getCurrentState();
      savePersistedState(tableId, state);
    }, persistenceDebounce);

    // Cleanup
    return () => {
      if (persistenceTimerRef.current) {
        clearTimeout(persistenceTimerRef.current);
      }
    };
  }, [
    sorting,
    columnFilters,
    columnVisibility,
    rowSelection,
    columnOrder,
    pagination,
    globalFilter,
    tableId,
    enablePersistence,
    persistenceDebounce,
    getCurrentState,
  ]);

  // Debounced state change callback
  useEffect(() => {
    if (!onStateChange) return;

    // Clear existing timer
    if (stateChangeTimerRef.current) {
      clearTimeout(stateChangeTimerRef.current);
    }

    // Set new timer for debounced callback
    stateChangeTimerRef.current = setTimeout(() => {
      const state = getCurrentState();
      onStateChange(state);
    }, persistenceDebounce);

    // Cleanup
    return () => {
      if (stateChangeTimerRef.current) {
        clearTimeout(stateChangeTimerRef.current);
      }
    };
  }, [
    sorting,
    columnFilters,
    columnVisibility,
    rowSelection,
    columnOrder,
    pagination,
    globalFilter,
    onStateChange,
    persistenceDebounce,
    getCurrentState,
  ]);

  // Create table instance
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // Sorting
    ...(enableSorting && {
      getSortedRowModel: getSortedRowModel(),
      onSortingChange: setSorting,
      manualSorting,
    }),
    // Filtering
    ...(enableFiltering && {
      getFilteredRowModel: getFilteredRowModel(),
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      manualFiltering,
    }),
    // Pagination
    ...(enablePagination && {
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination,
      manualPagination,
      ...(manualPagination && rowCount !== undefined && { rowCount }),
    }),
    // Row Selection
    ...(enableRowSelection && {
      onRowSelectionChange: setRowSelection,
      enableRowSelection: true,
    }),
    // Column Visibility
    ...(enableColumnVisibility && {
      onColumnVisibilityChange: setColumnVisibility,
    }),
    // Column Ordering
    ...(enableColumnOrdering && {
      onColumnOrderChange: setColumnOrder,
    }),
    // State
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      columnOrder,
      pagination,
      globalFilter,
    },
  });

  // Reset state to initial values
  const resetState = useCallback(() => {
    setSorting(initialState.sorting ?? []);
    setColumnFilters(initialState.columnFilters ?? []);
    setColumnVisibility(initialState.columnVisibility ?? {});
    setRowSelection(initialState.rowSelection ?? {});
    setColumnOrder(initialState.columnOrder ?? []);
    setGlobalFilter(initialState.globalFilter ?? '');
    setPagination(
      initialState.pagination ?? {
        pageIndex: 0,
        pageSize: defaultPageSize,
      }
    );

    // Clear persisted state
    if (enablePersistence) {
      clearPersistedState(tableId);
    }
  }, [initialState, defaultPageSize, tableId, enablePersistence]);

  // Export current state as JSON
  const exportState = useCallback((): string => {
    const state = getCurrentState();
    return JSON.stringify(state, null, 2);
  }, [getCurrentState]);

  // Import state from JSON
  const importState = useCallback(
    (stateJson: string) => {
      try {
        const state = JSON.parse(stateJson) as Partial<TableState>;

        if (state.sorting !== undefined) setSorting(state.sorting);
        if (state.columnFilters !== undefined) setColumnFilters(state.columnFilters);
        if (state.columnVisibility !== undefined) setColumnVisibility(state.columnVisibility);
        if (state.rowSelection !== undefined) setRowSelection(state.rowSelection);
        if (state.columnOrder !== undefined) setColumnOrder(state.columnOrder);
        if (state.globalFilter !== undefined) setGlobalFilter(state.globalFilter);
        if (state.pagination !== undefined) setPagination(state.pagination);
      } catch (error) {
        console.error('[useDataTable] Failed to import state:', error);
        throw new Error('Invalid state JSON format');
      }
    },
    []
  );

  // Clear persisted state utility
  const clearPersistedStateUtil = useCallback(() => {
    clearPersistedState(tableId);
  }, [tableId]);

  return {
    table,
    sorting,
    setSorting,
    columnVisibility,
    setColumnVisibility,
    rowSelection,
    setRowSelection,
    columnFilters,
    setColumnFilters,
    globalFilter,
    setGlobalFilter,
    columnOrder,
    setColumnOrder,
    pagination,
    setPagination,
    resetState,
    exportState,
    importState,
    clearPersistedState: clearPersistedStateUtil,
  };
}
